import {
  createContext,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemeMode = 'light' | 'dark';

/** Matches Base UI Portal `container` (element, shadow root, or ref). */
export type PortalContainer =
  | HTMLElement
  | ShadowRoot
  | RefObject<HTMLElement | ShadowRoot | null>;

/**
 * Global, cross-cutting runtime config shared by every `@hibro/ui-client`
 * component (架构方案 §5.7 / 见 16). Components read it via `useUiClientConfig`
 * instead of hardcoding behavior, so concerns like the active theme mode (and
 * later i18n, direction, density…) live in one place and can be inherited from
 * the host app or overridden per subtree.
 */
export interface UiClientConfig {
  /**
   * Active light/dark mode. Drives theme-dependent rendering inside the library
   * (e.g. a `resource` icon picks its light/dark asset). Defaults to inheriting
   * the host app's theme — documentElement's `data-theme`, written by `applyTheme`
   * (见 13) — and a provider may pin it explicitly.
   */
  themeMode: ThemeMode;
  /**
   * Default Portal mount for floating layers (Dialog / Sheet / Menu / Select /
   * Popover / Tooltip…). Resolution order per call site:
   * component `container` prop → this config → `document.body`.
   *
   * Hosts that must not portal into React's `#root` (createRoot conflict) or
   * need a constrained overlay host (e.g. custom window chrome) should set this
   * once on `UiClientConfigProvider`. Omit to keep the library default (`body`).
   */
  portalContainer?: HTMLElement;
}

function readInheritedThemeMode(): ThemeMode {
  return typeof document !== 'undefined' &&
    document.documentElement.dataset.theme === 'dark'
    ? 'dark'
    : 'light';
}

function defaultPortalContainer(): HTMLElement | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return document.body;
}

const DEFAULT_CONFIG: UiClientConfig = { themeMode: 'light' };

const UiClientConfigContext = createContext<UiClientConfig>(DEFAULT_CONFIG);

/**
 * Read the global `@hibro/ui-client` config (架构方案 §5.7 / 见 16). Without a
 * provider it returns the static default (`themeMode: 'light'`); wrap the app once
 * in `UiClientConfigProvider` to inherit the host theme and enable overrides.
 */
export function useUiClientConfig(): UiClientConfig {
  return useContext(UiClientConfigContext);
}

/**
 * Resolve the Portal mount element for a floating layer.
 *
 * Precedence: `override` (component prop) → `UiClientConfig.portalContainer` →
 * `document.body`. SSR / no-document returns `undefined` (Base UI then skips
 * custom container).
 */
export function usePortalContainer(
  override?: PortalContainer | null,
): PortalContainer | undefined {
  const { portalContainer } = useUiClientConfig();
  if (override != null) {
    return override;
  }
  return portalContainer ?? defaultPortalContainer();
}

export interface UiClientConfigProviderProps {
  children: ReactNode;
  /**
   * Pin the light/dark mode. Omit (default) to inherit the host app's theme by
   * observing documentElement's `data-theme` (见 13) and tracking changes live.
   */
  themeMode?: ThemeMode;
  /**
   * Default Portal container for all floating layers under this provider.
   * Individual components may still pass their own `container` to override.
   */
  portalContainer?: HTMLElement;
}

/**
 * Provide global config to `@hibro/ui-client` components (架构方案 §5.7 / 见 16).
 * By default every field inherits from the host app (the platform renderer): the
 * theme mode follows documentElement's `data-theme` and re-renders consumers on
 * change. Pass an explicit prop to override a field (custom config). This is the
 * single home for the theme-switch logic components used to hardcode.
 */
export function UiClientConfigProvider({
  children,
  themeMode,
  portalContainer,
}: UiClientConfigProviderProps): ReactNode {
  const inherit = themeMode === undefined;
  const [inheritedThemeMode, setInheritedThemeMode] = useState<ThemeMode>(
    readInheritedThemeMode,
  );

  useEffect(() => {
    if (!inherit) {
      return;
    }
    const el = document.documentElement;
    const read = (): void => {
      setInheritedThemeMode(el.dataset.theme === 'dark' ? 'dark' : 'light');
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      observer.disconnect();
    };
  }, [inherit]);

  const value = useMemo<UiClientConfig>(
    () => ({
      themeMode: themeMode ?? inheritedThemeMode,
      portalContainer,
    }),
    [themeMode, inheritedThemeMode, portalContainer],
  );

  return (
    <UiClientConfigContext.Provider value={value}>
      {children}
    </UiClientConfigContext.Provider>
  );
}
